import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ModuleState } from '@redux/modules/module'

import { BrandRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['brands']

type BrandFilters = {
  hidden?: 'true' | 'false',
  string?: string,
}

type UseBrandsOptions = {
  filters?: BrandFilters,
  performHttpRequests?: boolean,
  requestOptions?: BrandRequestOptions,
  sortKey?: 'brand' | 'provider',
}

function useBrands(options: UseBrandsOptions = {}) {
  const { filters } = options

  const {
    hidden: filterHidden,
    string,
  } = filters || {}

  const {
    updatedEntities: { brands: brandsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const brands = useSelector(reduxState => reduxState.entities.brands)

  const filteredBrands = useMemo(() => {
    const filtered = Object.values(brands).filter((brand) => {
      const { hidden, id, title } = brand

      const matchId = id.toString() === string
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      return matchHidden && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [brandsUpdatedAt, JSON.stringify(filters)])

  const hasFilteredBrands = !!filteredBrands.length
  const filteredBrandsCount = filteredBrands.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrands } = useReduxAction(
    'brands',
    'loadBrands',
    {
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    { shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrands,
    filteredBrandsCount,
    hasFilteredBrands,
    loading: loadingBrands,
  }
}

export default useBrands
